<template>
  <v-dialog v-model="internalOpened" max-width="1200" persistent>
    <v-card class="dialog-card">
      <v-card-title>
        Importar users
      </v-card-title>

      <v-card-text>
        <v-stepper v-model="step" class="import-stepper" flat>
          <v-stepper-header>
            <v-stepper-item
              :value="1"
              title="Ligação"
              subtitle="BD externa"
              :complete="step > 1"
            />

            <v-divider />

            <v-stepper-item
              :value="2"
              title="Mapear campos"
              subtitle="Campos users"
              :complete="step > 2"
            />

            <v-divider />

            <v-stepper-item
              :value="3"
              title="Pré-visualização"
              subtitle="Selecionar users"
              :complete="step > 3"
            />

            <v-divider />

            <v-stepper-item
              :value="4"
              title="Empresas"
              subtitle="Resolver NA"
              :complete="step > 4"
            />

            <v-divider />

            <v-stepper-item
              :value="5"
              title="Escolher acesso"
              subtitle="Opcional"
            />
          </v-stepper-header>
        </v-stepper>

        <!-- STEP 1 -->
        <div v-if="step === 1">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.host"
                label="Host"
                variant="outlined"
                :error-messages="errors.host"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.port"
                label="Port"
                type="number"
                variant="outlined"
                :error-messages="errors.port"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.database"
                label="Base de dados"
                variant="outlined"
                :error-messages="errors.database"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.table"
                label="Nome da tabela"
                variant="outlined"
                :error-messages="errors.table"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.fields"
                label="Campos a mostrar"
                placeholder="id,name,email,is_active"
                hint="Separar por vírgula. A password vem sempre para o backend, mas só aparece na tabela se escreveres password aqui."
                persistent-hint
                variant="outlined"
                :error-messages="errors.fields"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.username"
                label="Username"
                variant="outlined"
                :error-messages="errors.username"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.password"
                label="Password"
                type="password"
                variant="outlined"
                :error-messages="errors.password"
              />
            </v-col>
          </v-row>

          <div v-if="submitError" class="submit-error">
            {{ submitError }}
          </div>
        </div>

        <!-- STEP 2 -->
        <div v-if="step === 2">
          <div class="preview-header">
            <div>
              <h4>Mapear campos</h4>
              <p>Associa cada campo externo ao campo correto da tabela users.</p>
            </div>

            <v-btn variant="tonal" @click="step = 1">
              Voltar
            </v-btn>
          </div>

          <v-table class="preview-table">
            <thead>
              <tr>
                <th>Campo externo</th>
                <th>Guardar em users</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="field in importFields" :key="field">
                <td>
                  <strong>{{ field }}</strong>
                </td>

                <td>
                  <v-select
                    v-model="fieldMapping[field]"
                    :items="userTableFields"
                    item-title="title"
                    item-value="value"
                    label="Campo users"
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>

        <!-- STEP 3 -->
        <div v-if="step === 3">
          <div class="preview-header">
            <div>
              <h4>Pré-visualização</h4>
              <p>{{ selectedCount }} de {{ previewRows.length }} users selecionados</p>
            </div>

            <v-btn variant="tonal" @click="step = 2">
              Voltar
            </v-btn>
          </div>

          <v-table v-if="previewRows.length" class="preview-table">
            <thead>
              <tr>
                <th>Importar</th>
                <th v-for="field in previewFields" :key="field">
                  {{ field }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(row, index) in previewRows" :key="index">
                <td>
                  <v-switch
                    v-model="row.__import"
                    color="#4CAF50"
                    hide-details
                    inset
                  />
                </td>

                <td v-for="field in previewFields" :key="field">
                  {{ row[field] }}
                </td>
              </tr>
            </tbody>
          </v-table>

          <div v-else class="empty-state">
            <div class="empty-icon">i</div>
            <h4>Sem dados</h4>
            <p>Não foram encontrados users.</p>
          </div>
        </div>

        <!-- STEP 4 -->
        <div v-if="step === 4">
          <div class="preview-header">
            <div>
              <h4>Resolver empresas</h4>
              <p>
                Existem {{ rowsWithNA.length }} users selecionados com company_origin = NA.
              </p>
            </div>

            <v-btn variant="tonal" @click="step = 3">
              Voltar
            </v-btn>
          </div>

          <v-switch
            v-model="ignoreCompanyStep"
            label="Ignorar este passo"
            color="#4CAF50"
            inset
            class="mb-4"
          />

          <v-alert
            v-if="ignoreCompanyStep"
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            Os users com company_origin = NA não serão inseridos.
          </v-alert>

          <template v-if="!ignoreCompanyStep">
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="bulkCompany"
                  :items="companies"
                  item-title="name"
                  item-value="name"
                  label="Aplicar empresa a todos"
                  variant="outlined"
                  density="comfortable"
                  :loading="loadingCompanies"
                  :disabled="loadingCompanies"
                  clearable
                  @update:model-value="applyBulkCompany"
                />
              </v-col>
            </v-row>

            <v-table v-if="rowsWithNA.length" class="preview-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Empresa original</th>
                  <th>Empresa destino</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="row in rowsWithNA" :key="row.__index">
                  <td>{{ getRowCode(row) }}</td>
                  <td>{{ getRowName(row) }}</td>
                  <td>{{ row.company_origin }}</td>
                  <td>
                    <v-select
                      v-model="previewRows[row.__index].company_override"
                      :items="companies"
                      item-title="name"
                      item-value="name"
                      label="Empresa"
                      variant="outlined"
                      density="compact"
                      hide-details
                      :loading="loadingCompanies"
                    />
                  </td>
                </tr>
              </tbody>
            </v-table>

            <div v-if="companyStepError" class="submit-error">
              {{ companyStepError }}
            </div>
          </template>
        </div>

        <!-- STEP 5 -->
        <div v-if="step === 5">
          <div class="preview-header">
            <div>
              <h4>Escolher acesso</h4>
              <p>
                Opcional. Podes escolher uma aplicação destino agora ou importar os users sem lhes atribuir aplicação.
              </p>
            </div>

            <v-btn variant="tonal" @click="goBackFromAccessStep">
              Voltar
            </v-btn>
          </div>

          <v-alert
            type="info"
            variant="tonal"
            class="mb-4"
          >
            Se não escolheres nenhuma aplicação, os users serão importados/criados sem acesso associado.
          </v-alert>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="selectedAccessId"
                :items="accesses"
                item-title="name"
                item-value="id"
                label="Aplicação opcional"
                variant="outlined"
                :loading="loadingAccesses"
                :disabled="loadingAccesses"
                :error-messages="errors.access_id"
                clearable
                no-data-text="Sem aplicações disponíveis. Podes continuar sem escolher."
              />
            </v-col>
          </v-row>

          <div v-if="!loadingAccesses && accesses.length === 0" class="access-empty-info">
            Não existem aplicações disponíveis para selecionar. Podes importar os users sem aplicação destino.
          </div>

          <div v-if="submitError" class="submit-error">
            {{ submitError }}
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn variant="text" @click="close">
          Cancelar
        </v-btn>

        <v-btn
          v-if="step === 1"
          color="primary"
          :loading="loadingPreview"
          :disabled="loadingPreview"
          @click="loadPreview"
        >
          Continuar
        </v-btn>

        <v-btn
          v-if="step === 2"
          color="primary"
          @click="step = 3"
        >
          Continuar
        </v-btn>

        <v-btn
          v-if="step === 3"
          color="primary"
          :disabled="selectedCount === 0"
          @click="goToCompanyOrAccessStep"
        >
          Continuar
        </v-btn>

        <v-btn
          v-if="step === 4"
          color="primary"
          @click="goToAccessStep"
        >
          Continuar
        </v-btn>

        <v-btn
          v-if="step === 5"
          color="primary"
          :loading="loadingImport"
          :disabled="selectedCount === 0 || loadingImport"
          @click="confirmImport"
        >
          Importar selecionados
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Users from '@/api/Users'
import Accesses from '@/api/Accesses'
import Company from '@/api/Company'

export default {
  name: 'ImportUsers',

  props: {
    opened: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:opened', 'import'],

  data() {
    return {
      step: 1,
      loadingPreview: false,
      loadingAccesses: false,
      loadingCompanies: false,
      loadingImport: false,
      submitError: '',
      companyStepError: '',
      selectedAccessId: null,
      accesses: [],
      companies: [],
      ignoreCompanyStep: false,
      bulkCompany: null,

      form: {
        host: '',
        port: '',
        database: '',
        table: '',
        fields: '',
        username: '',
        password: ''
      },

      errors: {
        host: '',
        port: '',
        database: '',
        table: '',
        fields: '',
        username: '',
        password: '',
        access_id: ''
      },

      previewFields: [],
      importFields: [],
      previewRows: [],
      fieldMapping: {},

      userTableFields: [
        { title: 'Ignorar', value: null },
        { title: 'name', value: 'name' },
        { title: 'email', value: 'email' },
        { title: 'username', value: 'username' },
        { title: 'password', value: 'password' },
        { title: 'is_active', value: 'is_active' },
        { title: 'is_external', value: 'is_external' },
        { title: 'funcionario_codigo', value: 'funcionario_codigo' },
        { title: 'telephone', value: 'telephone' },
        { title: 'company', value: 'company' }
      ]
    }
  },

  computed: {
    internalOpened: {
      get() {
        return this.opened
      },
      set(value) {
        this.$emit('update:opened', value)
      }
    },

    selectedCount() {
      return this.previewRows.filter(row => row.__import).length
    },

    rowsWithNA() {
      return this.previewRows
        .map((row, index) => ({
          ...row,
          __index: index
        }))
        .filter(row => row.__import && row.company_origin === 'NA')
    }
  },

  methods: {
    resetErrors() {
      this.errors = {
        host: '',
        port: '',
        database: '',
        table: '',
        fields: '',
        username: '',
        password: '',
        access_id: ''
      }

      this.submitError = ''
      this.companyStepError = ''
    },

    validateStepOne() {
      this.resetErrors()

      let valid = true

      if (!this.form.host?.trim()) {
        this.errors.host = 'O host é obrigatório.'
        valid = false
      }

      if (!this.form.port) {
        this.errors.port = 'A port é obrigatória.'
        valid = false
      }

      if (!this.form.database?.trim()) {
        this.errors.database = 'A base de dados é obrigatória.'
        valid = false
      }

      if (!this.form.table?.trim()) {
        this.errors.table = 'A tabela é obrigatória.'
        valid = false
      }

      if (!this.form.fields?.trim()) {
        this.errors.fields = 'Indica pelo menos um campo.'
        valid = false
      }

      return valid
    },

    getVisibleFields() {
      if (this.form.fields.trim() === '*') {
        return this.importFields
      }

      const visibleFields = this.form.fields
        .split(',')
        .map(field => field.trim())
        .filter(Boolean)

      if (this.importFields.includes('company_origin') && !visibleFields.includes('company_origin')) {
        visibleFields.push('company_origin')
      }

      return visibleFields
    },

    guessUserField(externalField) {
      const field = externalField.toLowerCase().trim()

      const matches = {
        id: null,
        name: 'name',
        nome: 'name',
        full_name: 'name',
        fullname: 'name',
        email: 'email',
        mail: 'email',
        e_mail: 'email',
        username: 'username',
        user_name: 'username',
        utilizador: 'username',
        login: 'username',
        password: 'password',
        pass: 'password',
        senha: 'password',
        palavra_passe: 'password',
        is_active: 'is_active',
        active: 'is_active',
        ativo: 'is_active',
        estado: 'is_active',
        is_external: 'is_external',
        external: 'is_external',
        externo: 'is_external',
        funcionario_codigo: 'funcionario_codigo',
        codigo_funcionario: 'funcionario_codigo',
        employee_code: 'funcionario_codigo',
        codigo: 'funcionario_codigo',
        code: 'funcionario_codigo',
        telephone: 'telephone',
        phone: 'telephone',
        telefone: 'telephone',
        telemovel: 'telephone',
        mobile: 'telephone',
        company: 'company',
        company_origin: 'company',
        empresa: 'company'
      }

      return matches[field] ?? null
    },

    buildAutomaticMapping() {
      const mapping = {}

      this.importFields.forEach(field => {
        mapping[field] = this.guessUserField(field)
      })

      if (this.importFields.includes('company_origin')) {
        mapping.company_origin = 'company'
      }

      this.fieldMapping = mapping
    },

    async loadPreview() {
      if (!this.validateStepOne()) return

      this.loadingPreview = true
      this.submitError = ''

      try {
        const response = await Users.importPreview({
          host: this.form.host.trim(),
          port: Number(this.form.port),
          database: this.form.database.trim(),
          table: this.form.table.trim(),
          fields: this.form.fields.trim(),
          username: this.form.username.trim(),
          password: this.form.password
        })

        this.importFields = response.data.fields || []
        this.previewFields = this.getVisibleFields()

        this.previewRows = (response.data.data || []).map(row => {
          const isActive = row.is_active

          return {
            ...row,
            company_override: null,
            __import: !(isActive === false || isActive === 0 || isActive === '0')
          }
        })

        this.buildAutomaticMapping()

        this.step = 2
      } catch (error) {
        this.handleError(error, 'Erro ao obter pré-visualização.')
        console.error('Erro ao obter preview:', error?.response?.data || error)
      } finally {
        this.loadingPreview = false
      }
    },

    async loadAccesses() {
      this.loadingAccesses = true
      this.submitError = ''

      try {
        const response = await Accesses.getAll()

        this.accesses = response.data.data || response.data || []
      } catch (error) {
        this.accesses = []
        this.submitError = ''
        console.error('Erro ao carregar accesses:', error?.response?.data || error)
      } finally {
        this.loadingAccesses = false
      }
    },

    async loadCompanies() {
      this.loadingCompanies = true
      this.submitError = ''

      try {
        const response = await Company.getAll()

        this.companies = response.data.data || response.data || []
      } catch (error) {
        this.submitError = 'Erro ao carregar empresas.'
        console.error('Erro ao carregar companies:', error?.response?.data || error)
      } finally {
        this.loadingCompanies = false
      }
    },

    async goToCompanyOrAccessStep() {
      this.resetErrors()

      if (this.rowsWithNA.length > 0) {
        if (!this.companies.length) {
          await this.loadCompanies()
        }

        this.step = 4
        return
      }

      await this.goToAccessStep()
    },

    async goToAccessStep() {
      this.companyStepError = ''

      if (!this.ignoreCompanyStep) {
        const missingCompany = this.rowsWithNA.some(row => {
          return !this.previewRows[row.__index].company_override
        })

        if (this.rowsWithNA.length > 0 && missingCompany) {
          this.companyStepError = 'Escolhe uma empresa para todos os users ou ativa "Ignorar este passo".'
          return
        }
      }

      if (!this.accesses.length) {
        await this.loadAccesses()
      }

      this.errors.access_id = ''
      this.step = 5
    },

    goBackFromAccessStep() {
      if (this.rowsWithNA.length > 0) {
        this.step = 4
      } else {
        this.step = 3
      }
    },

    applyBulkCompany(value) {
      this.previewRows = this.previewRows.map(row => {
        if (row.__import && row.company_origin === 'NA') {
          return {
            ...row,
            company_override: value
          }
        }

        return row
      })
    },

    validateStepFive() {
      this.errors.access_id = ''
      return true
    },

    getSelectedRows() {
      return this.previewRows
        .filter(row => row.__import)
        .map(row => {
          const cleaned = {}

          this.importFields.forEach(field => {
            cleaned[field] = row[field]
          })

          if (row.company_origin) {
            if (row.company_origin === 'NA') {
              if (this.ignoreCompanyStep) {
                cleaned.__skip = true
              } else {
                cleaned.company = row.company_override
                cleaned.company_origin = row.company_override
              }
            } else {
              cleaned.company = row.company_origin
              cleaned.company_origin = row.company_origin
            }
          }

          return cleaned
        })
        .filter(row => !row.__skip)
    },

    getRowCode(row) {
      return row.funcionario_codigo || row.codigo || row.code || row.employee_code || row.id || ''
    },

    getRowName(row) {
      return row.name || row.nome || row.full_name || row.fullname || row.username || ''
    },

    async confirmImport() {
      if (!this.validateStepFive()) return

      this.loadingImport = true
      this.submitError = ''

      try {
        const payload = {
          access_id: this.selectedAccessId || null,
          field_mapping: this.fieldMapping,
          visible_fields: this.previewFields,
          fields: this.importFields,
          rows: this.getSelectedRows(),
          source: {
            host: this.form.host,
            port: Number(this.form.port),
            database: this.form.database,
            table: this.form.table
          }
        }

        const response = await Users.importStore(payload)

        this.$emit('import', response.data)

        this.close()
      } catch (error) {
        this.handleError(error, 'Erro ao importar users.')
        console.error('Erro ao importar users:', error?.response?.data || error)
      } finally {
        this.loadingImport = false
      }
    },

    handleError(error, defaultMessage) {
      const responseData = error?.response?.data

      if (responseData?.errors) {
        this.errors.host = responseData.errors.host?.[0] || ''
        this.errors.port = responseData.errors.port?.[0] || ''
        this.errors.database = responseData.errors.database?.[0] || ''
        this.errors.table = responseData.errors.table?.[0] || ''
        this.errors.fields = responseData.errors.fields?.[0] || ''
        this.errors.username = responseData.errors.username?.[0] || ''
        this.errors.password = responseData.errors.password?.[0] || ''
        this.errors.access_id = responseData.errors.access_id?.[0] || ''
      } else {
        this.submitError = responseData?.message || responseData?.error || defaultMessage
      }
    },

    close() {
      this.internalOpened = false
      this.step = 1
      this.loadingPreview = false
      this.loadingAccesses = false
      this.loadingCompanies = false
      this.loadingImport = false
      this.submitError = ''
      this.companyStepError = ''
      this.selectedAccessId = null
      this.accesses = []
      this.previewFields = []
      this.importFields = []
      this.previewRows = []
      this.fieldMapping = {}
      this.ignoreCompanyStep = false
      this.bulkCompany = null
    }
  }
}
</script>

<style scoped>
.dialog-card {
  border-radius: 18px;
}

.import-stepper {
  margin-bottom: 24px;
  box-shadow: none;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
}

.submit-error {
  margin-top: 12px;
  color: #dc2626;
  font-size: 0.95rem;
}

.access-empty-info {
  margin-top: 8px;
  color: #64748b;
  font-size: 0.95rem;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.preview-header h4 {
  margin: 0;
}

.preview-header p {
  margin: 4px 0 0;
  color: #64748b;
}

.preview-table {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.preview-table th,
.preview-table td {
  white-space: nowrap;
}

.empty-state {
  text-align: center;
  padding: 40px;
}

.empty-icon {
  width: 40px;
  height: 40px;
  background: #e2e8f0;
  border-radius: 50%;
  margin: 0 auto 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>